package com.karawek.core.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.karawek.core.ui.theme.CoreShapes
import com.karawek.core.ui.theme.CoreSpacing
import com.karawek.core.ui.theme.CoreTypography
import com.karawek.core.ui.theme.CoreTheme

@Composable
fun CoreOtpInput(
    value: String,
    onValueChange: (String) -> Unit,
    length: Int = 6,
    modifier: Modifier = Modifier,
    boxSize: Dp = 48.dp,
    autoFocus: Boolean = true,
    onComplete: (String) -> Unit = {},
) {
    val colors = CoreTheme.colors
    val focusRequester = remember { FocusRequester() }

    val sanitized = remember(value, length) {
        value.filter(Char::isDigit).take(length)
    }

    // ✅ Track completion edge
    var wasComplete by remember { mutableStateOf(false) }
    val isComplete = sanitized.length == length

    LaunchedEffect(isComplete) {
        if (isComplete && !wasComplete) {
            wasComplete = true
            onComplete(sanitized)
        }
        if (!isComplete) {
            wasComplete = false
        }
    }

    // ✅ Auto-focus when screen is shown
    LaunchedEffect(autoFocus) {
        if (autoFocus) {
            focusRequester.requestFocus()
        }
    }

    Box(
        modifier = modifier
            .clickable { focusRequester.requestFocus() },
        contentAlignment = Alignment.Center,
    ) {

        /* ---------- Hidden input ---------- */

        BasicTextField(
            value = sanitized,
            onValueChange = { input ->
                val filtered = input.filter(Char::isDigit).take(length)
                onValueChange(filtered)
            },
            modifier = Modifier
                .focusRequester(focusRequester)
                .size(1.dp),
            keyboardOptions = KeyboardOptions(
                keyboardType = KeyboardType.Number,
            ),
            singleLine = true,
        )

        /* ---------- Visual boxes ---------- */

        Row(
            horizontalArrangement = Arrangement.spacedBy(CoreSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            repeat(length) { index ->
                val digit = sanitized.getOrNull(index)?.toString().orEmpty()

                val borderColor = when {
                    isComplete -> colors.primary
                    digit.isNotEmpty() -> colors.primary
                    else -> colors.divider
                }

                Box(
                    modifier = Modifier
                        .size(boxSize)
                        .border(
                            width = 1.dp,
                            color = borderColor,
                            shape = CoreShapes.md,
                        )
                        .background(
                            color = colors.surface,
                            shape = CoreShapes.md,
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        text = digit,
                        style = CoreTypography.headline,
                        color = colors.textPrimary,
                    )
                }
            }
        }
    }
}
